import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const manager = u?.is_admin || u?.role_category === "rc_approver";
      navigate(manager ? "/manage/overview" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex kp-paper">
      {/* Editorial hero panel */}
      <div className="hidden lg:flex flex-col justify-between w-[52%] bg-primary text-primary-foreground p-14 relative overflow-hidden">
        <div className="absolute inset-0 kp-grid-bg opacity-[0.08]" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />

        <div className="relative flex items-center gap-2.5 kp-in">
          <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 backdrop-blur flex items-center justify-center border border-primary-foreground/20">
            <Bot className="w-6 h-6" />
          </div>
          <div>
            <p className="font-display text-xl leading-tight">Ask <span className="italic">Z-Method</span></p>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary-foreground/60">Enterprise Process Portal</p>
          </div>
        </div>

        <div className="relative kp-up" style={{ animationDelay: "80ms" }}>
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-secondary/90 mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Issue 01 · Grounded knowledge
          </p>
          <h1 className="font-display text-[68px] leading-[0.95] tracking-tight">
            Ask.<br />
            <span className="italic text-accent">Understand.</span><br />
            Get guided<br />through it.
          </h1>
          <p className="mt-6 text-primary-foreground/70 max-w-md text-[15px] leading-relaxed">
            A role-aware AI process assistant for your internal portals. Step-by-step
            guidance grounded in your SOPs, workflows and FAQs — with sources, never guesses.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["SOPs", "Workflows", "RBAC", "Escalation Matrix", "Analytics"].map((t) => (
              <span key={t} className="text-[11px] font-mono uppercase tracking-wider rounded-full bg-primary-foreground/8 border border-primary-foreground/15 px-3 py-1.5">
                {t}
              </span>
            ))}
          </div>
        </div>

        <p className="relative text-xs text-primary-foreground/50 flex items-center gap-1.5 font-mono uppercase tracking-widest">
          <ShieldCheck className="w-3.5 h-3.5" /> Enterprise RBAC · Grounded · No hallucination
        </p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm kp-up">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Bot className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-xl">Ask <span className="italic text-primary">Z-Method</span></span>
          </div>

          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2">Chapter 01</p>
          <h2 className="font-display text-4xl text-foreground leading-tight">Welcome back.</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-8">Sign in to your workspace</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" className="mt-2 h-11 bg-card" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="mt-2 h-11 bg-card" required />
            </div>
            {error && <p className="text-sm text-destructive font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11 rounded-full font-semibold" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
